<template>
  <div class="step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="#e8612f">mdi-calendar-edit</v-icon>
      </div>
      <div class="step-content">
        <h2 class="title">Schedule Setup</h2>
        <p class="desc">
          Define availability and working hours.
        </p>
      </div>
    </div>

    <div class="form-field">
      <div class="label">
        <span>Working Days</span>
      </div>
      <div class="working-days-selection">
        <v-btn
          v-for="day in days"
          :key="day.key"
          :color="selectedDays.includes(day.key) ? 'primary' : ''"
          :variant="selectedDays.includes(day.key) ? 'flat' : 'outlined'"
          @click="toggleDay(day.key)"
        >
          {{ day.shortLabel }}
        </v-btn>
      </div>
    </div>

    <div class="form-field">
      <div class="label">
        <span>Working Hours</span>
      </div>
      <v-row>
        <v-col cols="6">
          <AppTextField
            v-model="startTime"
            type="time"
            label="Start Time"
            label-icon="mdi-clock-time-four-outline"
          />
        </v-col>
        <v-col cols="6">
          <AppTextField
            v-model="endTime"
            type="time"
            label="End Time"
            label-icon="mdi-clock-time-eight-outline"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";

defineProps<{
  current_step?: number;
  errors?: Record<string, any> | null;
  profileInfo?: any;
}>();

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

const days: { key: DayKey; label: string; shortLabel: string }[] = [
  { key: "mon", label: "Monday", shortLabel: "Mon" },
  { key: "tue", label: "Tuesday", shortLabel: "Tue" },
  { key: "wed", label: "Wednesday", shortLabel: "Wed" },
  { key: "thu", label: "Thursday", shortLabel: "Thu" },
  { key: "fri", label: "Friday", shortLabel: "Fri" },
  { key: "sat", label: "Saturday", shortLabel: "Sat" },
  { key: "sun", label: "Sunday", shortLabel: "Sun" },
];

const selectedDays = reactive<DayKey[]>(["mon", "tue", "wed", "thu", "fri"]);
const startTime = ref("09:00");
const endTime = ref("17:00");

function toggleDay(dayKey: DayKey) {
  const index = selectedDays.indexOf(dayKey);
  if (index > -1) {
    selectedDays.splice(index, 1);
  } else {
    selectedDays.push(dayKey);
  }
}

function getValues() {
  const schedule: Record<DayKey, { enabled: boolean; open: string; close: string }> = {} as any;
  days.forEach(day => {
    schedule[day.key] = {
      enabled: selectedDays.includes(day.key),
      open: startTime.value,
      close: endTime.value,
    };
  });
  return { schedule };
}

defineExpose({ getValues });
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.form-field {
  margin-top: 16px;
}

.label {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  margin-bottom: 12px;
}

.working-days-selection {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
</style>
